"use client";

import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { File, Download, Trash2 } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";

export const DocumentUploader = () => {
  const { company } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const handleUpload = async () => {
    if (!file || !company?.id) return;

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${company.id}/documents/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const publicUrl = supabase.storage.from("documents").getPublicUrl(filePath).data.publicUrl;

      const { error: dbError } = await supabase
        .from("documents")
        .insert({
          name: file.name,
          file_url: publicUrl,
          file_type: file.type,
          size_bytes: file.size,
          company_id: company.id,
        });

      if (dbError) throw dbError;

      showSuccess("Document uploaded!");
      setFile(null);
    } catch (error) {
      showError("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const { data: documents, error: docError } = useQuery({
    queryKey: ["documents", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];
      const { data } = await supabase
        .from("documents")
        .select("*")
        .eq("company_id", company.id);
      return data || [];
    },
    enabled: !!company?.id,
  });

  if (docError) {
    console.error("Error fetching documents");
  }

  const handleDelete = async (id: string, filePath: string) => {
    const { error: deleteError } = await supabase
      .from("documents")
      .delete()
      .eq("id", id);

    if (deleteError) {
      showError("Delete failed");
      return;
    }

    await supabase.storage.from("documents").remove([filePath]);
    showSuccess("Document deleted!");
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Upload Document</CardTitle>
        </CardHeader>
        <CardContent>
          <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Button onClick={handleUpload} disabled={!file || uploading} className="mt-2">
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
        </CardHeader>
        <CardContent>
          {documents?.map((doc: any) => (
            <div key={doc.id} className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center space-x-2">
                <File className="h-4 w-4" />
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                  {doc.name}
                </a>
              </div>
              <div className="space-x-2">
                <Button variant="outline" size="sm" asChild>
                  <a href={doc.file_url} download>
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </a>
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(doc.id, doc.file_url.split("/").slice(-2).join("/"))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
          {documents?.length === 0 && <p className="text-muted-foreground text-center py-8">No documents yet.</p>}
        </CardContent>
      </Card>
    </div>
  );
};